import { useState, useEffect } from "react";
import { Container, Nav, NavItem, NavLink } from "reactstrap";
import Select from 'react-select';

import Header, { BotStats } from "components/Headers/Header.js";
import Matriz from "../layouts/Matriz";
import PlanBot from "../layouts/PlanBot";
import ApiCall from '../utils/apis';
import LogViewer from "../layouts/LogViewer";
import Grafica from "../layouts/Grafica";
import Bots from "./Bots";
import Settings from "./Settings";
import Billing from "./Billing";
import Estrategias from "./Estrategias";
import { clearSession, getSession, isSuperAdmin } from "../utils/session";

const selectStyles = {
  container: base => ({ ...base, width: 300, display: 'inline-block', verticalAlign: 'middle' }),
  control: base => ({
    ...base,
    borderRadius: 8,
    borderColor: '#dee2e6',
    boxShadow: 'none',
    fontSize: 15,
    fontWeight: 600,
    minHeight: 36,
    '&:hover': { borderColor: '#11cdef' },
  }),
  singleValue: base => ({ ...base, color: '#32325d' }),
  option: (base, { isFocused }) => ({
    ...base,
    background: isFocused ? '#e8f8ff' : 'white',
    color: '#32325d',
    fontWeight: 600,
  }),
};

const BOT_TABS = [
  { key: "matriz", label: "Plan", icon: "ni ni-compass-04" },
  { key: "logs", label: "Logs", icon: "ni ni-bullet-list-67" },
  { key: "grafica", label: "Gráfica", icon: "fas fa-chart-line" },
];

const Index = (props) => {
  const [matriz, setMatriz] = useState(undefined);
  const [statusBot, setStatusBot] = useState(undefined);
  const [screm, setScrem] = useState("bots");
  const [bots, setBots] = useState([]);           // los del usuario
  const [otherBots, setOtherBots] = useState([]); // solo super admin: los de los demas
  const [botId, setBotId] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [billing, setBilling] = useState(null);       // comisiones del usuario
  const [reported, setReported] = useState(0);        // solo super admin: pagos por aprobar

  const allBots = [...bots, ...otherBots];
  const bot = allBots.find(b => b.id === botId);
  const symbol = bot ? bot.coin : "";

  async function fetchBots() {
    const res = await ApiCall({ call: isSuperAdmin() ? `bots?all=1` : `bots`, method: "GET" });
    if (res && res.bots) {
      const session = getSession();
      const myId = session && session.user ? session.user.id : null;
      const mine = res.bots.filter(b => b.userId === myId);
      setBots(mine);
      setOtherBots(res.bots.filter(b => b.userId !== myId));
      if (!res.bots.find(b => b.id === botId)) {
        setBotId(mine.length > 0 ? mine[0].id : null);
      }
    }
  }

  async function fetchBilling() {
    const res = await ApiCall({ call: `billing`, method: "GET" });
    if (res && !res.error) setBilling(res);
    if (isSuperAdmin()) {
      const admin = await ApiCall({ call: `billing/admin`, method: "GET" });
      if (admin && admin.invoices) setReported(admin.invoices.filter(i => i.status === 'reported').length);
    }
  }

  useEffect(() => {
    fetchBots();
    fetchBilling();
  }, []);

  async function api() {
    if (!botId) {
      setMatriz(undefined);
      setStatusBot(undefined);
      return;
    }
    const resultStatus = await ApiCall({ call: `statusBot?botId=${botId}`, method: "GET" });
    // Los bots sin matriz (DCA Scalps, Short, Scalping) traen su plan en el status
    const result = resultStatus && resultStatus.plan ? undefined
      : await ApiCall({ call: `matrizCuantitativaIndexBot?botId=${botId}`, method: "GET" });
    setMatriz(result);
    setStatusBot(resultStatus);
    setLastUpdate(new Date());
  }

  async function refresh() {
    await fetchBots();
    await fetchBilling();
    await api();
  }

  useEffect(() => {
    setMatriz(undefined);
    api();
  }, [botId]);

  // En la pestaña Plan el estado del bot se actualiza solo
  useEffect(() => {
    if (screm !== "matriz" || !botId) return undefined;
    const timer = setInterval(api, 15000);
    return () => clearInterval(timer);
  }, [screm, botId]);

  const logout = () => {
    clearSession();
    props.history.push('/auth/login');
  };

  const optionLabel = (b, owner) => `${owner ? `[${b.userLogin}] ` : ''}${b.coin} · ${b.amount} USDT${b.status === 'running' ? '' : ' (detenido)'}`;
  const botOptions = [
    ...bots.map(b => ({ value: b.id, label: optionLabel(b, false) })),
    ...otherBots.map(b => ({ value: b.id, label: optionLabel(b, true) })),
  ];
  const selectedOption = botOptions.find(o => o.value === botId) || null;
  const running = statusBot && statusBot.running;
  const hasMatriz = running && matriz && !matriz.error && Object.keys(matriz).length > 0;

  // Avisos de la barra: bots corriendo y comisiones por atender
  const pending = billing && billing.invoices ? billing.invoices.filter(i => i.status === 'pending') : [];
  const overdue = (billing && billing.blocked) || pending.some(i => i.overdue);
  const badges = {
    bots: { count: bots.filter(b => b.status === 'running').length, color: 'success', title: 'Bots corriendo' },
    billing: isSuperAdmin()
      ? { count: reported, color: 'info', title: 'Pagos por aprobar' }
      : { count: pending.length, color: overdue ? 'danger' : 'warning', title: overdue ? 'Comisión vencida' : 'Comisión por pagar' },
  };

  const emptyMessage = (text) => (
    <div style={{ marginTop: 40, textAlign: 'center', color: '#8898aa', fontWeight: 600 }}>{text}</div>
  );

  const botScreen = () => {
    if (!bot) return emptyMessage('Crea un bot en la pestaña Bots para empezar.');
    if (screm === "grafica") return <Grafica symbol={symbol} botId={botId} />;
    if (screm === "logs") return <LogViewer symbol={symbol} botId={botId} />;
    if (running && statusBot.plan) return <PlanBot symbol={symbol} statusBot={statusBot} />;
    if (!hasMatriz) return emptyMessage('Bot detenido. Levántalo en la pestaña Bots para ver su plan.');
    return <Matriz symbol={symbol} setSymbol={() => {}} matriz={matriz} statusBot={statusBot} refresh={api} />;
  };

  return (
    <>
      <Header refresh={refresh} setScrem={setScrem} screm={screm} logout={logout} badges={badges} />

      {screm === "bots" ? (
        <Container fluid>
          <Bots bots={bots} otherBots={otherBots} reload={refresh} onSelect={(id) => { setBotId(id); setScrem("matriz"); }} onBilling={() => setScrem("billing")} />
        </Container>
      ) : screm === "billing" ? (
        <Container fluid>
          <Billing onChange={fetchBilling} />
        </Container>
      ) : screm === "info" ? (
        <Container fluid>
          <Estrategias />
        </Container>
      ) : screm === "settings" ? (
        <Container fluid>
          <Settings />
        </Container>
      ) : (
        <>
          <Container fluid>
            <div className="bot-bar">
              <span className="bot-bar-label">Bot:</span>
              <Select
                value={selectedOption}
                options={botOptions}
                onChange={opt => setBotId(opt.value)}
                styles={selectStyles}
                isSearchable={false}
              />
              <Nav pills>
                {BOT_TABS.map(tab => (
                  <NavItem key={tab.key}>
                    <NavLink
                      href="#"
                      active={screm === tab.key}
                      onClick={e => { e.preventDefault(); setScrem(tab.key); }}
                    >
                      <i className={tab.icon} />
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </div>
            {bot && <BotStats statusBot={statusBot} lastUpdate={lastUpdate} />}
          </Container>

          <Container fluid>
            {botScreen()}
          </Container>
        </>
      )}
    </>
  );
};

export default Index;
